import { useEffect, useState } from 'react'
import { api, mediaUrl } from '@/lib/api'

export default function ResourceManager({
  title,
  endpoint,
  folder,
  fields,
  imageField,
}) {
  const [rows, setRows] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({})
  const [file, setFile] = useState(null)

  const load = async () => {
    const { data } = await api.get(`${endpoint}/admin`)
    setRows(data.data || [])
  }

  useEffect(() => { load().catch(() => setRows([])) }, [endpoint])

  const startCreate = () => {
    const initial = {}
    fields.forEach((f) => { initial[f.name] = f.default ?? '' })
    setForm(initial)
    setFile(null)
    setEditing('new')
  }

  const startEdit = (row) => {
    const next = {}
    fields.forEach((f) => { next[f.name] = row[f.name] ?? '' })
    setForm(next)
    setFile(null)
    setEditing(row)
  }

  const save = async (e) => {
    e.preventDefault()
    const body = new FormData()
    fields.forEach((f) => body.append(f.name, form[f.name] ?? ''))
    body.append('folder', folder)
    if (file) body.append('image', file)
    if (editing === 'new') await api.post(endpoint, body)
    else await api.put(`${endpoint}/${editing.id}`, body)
    setEditing(null)
    await load()
  }

  const remove = async (row) => {
    if (!confirm('Delete this item?')) return
    await api.delete(`${endpoint}/${row.id}`)
    await load()
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <button onClick={startCreate} className="admin-btn">Add</button>
      </div>
      {editing && (
        <form onSubmit={save} className="admin-glass rounded-2xl p-5 mb-6 grid md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <label key={f.name} className={f.full ? 'md:col-span-2' : ''}>
              <span className="text-sm text-slate-400">{f.label}</span>
              {f.type === 'textarea' ? (
                <textarea className="admin-input mt-1" rows={4} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              ) : f.type === 'select' ? (
                <select className="admin-input mt-1" value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input className="admin-input mt-1" value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              )}
            </label>
          ))}
          {imageField && (
            <label className="md:col-span-2">
              <span className="text-sm text-slate-400">{imageField}</span>
              <input type="file" accept="image/*" className="block mt-2" onChange={(e) => setFile(e.target.files[0])} />
            </label>
          )}
          <div className="md:col-span-2 flex gap-3">
            <button className="admin-btn">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-white/10">Cancel</button>
          </div>
        </form>
      )}
      <div className="admin-glass rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-400">
            <tr><th className="text-left p-3">Item</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-slate-800">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {(row.image || row.logo || row.featuredImage || row.profileImage) && (
                      <img src={mediaUrl(row.image || row.logo || row.featuredImage || row.profileImage)} alt="" className="w-12 h-12 object-cover rounded-lg" />
                    )}
                    <div>
                      <p className="font-medium">{row.title || row.name || row.clientName || row.question}</p>
                      <p className="text-slate-400 text-xs">{row.companyName || row.company || row.category || row.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3">{row.isPublished ? 'Published' : 'Hidden'}</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(row)} className="mr-3 text-blue-400">Edit</button>
                  <button onClick={() => remove(row)} className="text-red-400">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
